import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PortalApiError, portalApi } from '../api';
import { clearSession, consumeDeletionCancelled, getAccessToken } from '../authStorage';
import { getDefaultMeetingNoLength } from '../config';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../constants';
import { withDeviceJoinPrompt } from '../deviceJoin';
import { generateMeetingNo, navigateToMeeting } from '../functions';
import { canRejoinHistory, findRecordingForMeeting, formatHomeTodayLabel, formatHomeWeekdayLabel, formatMeetingNo, formatTimeRangeHm, groupPortalMeetingsByDate } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { userMediaUrl } from '../profileMedia';
import { useIsPortalMobile } from '../useIsPortalMobile';
import CreateMeetingForm from './CreateMeetingForm';
import HistoryPage from './HistoryPage';
import JoinMeetingForm from './JoinMeetingForm';
import MeetingDetailPage from './MeetingDetailPage';
import ProfilePage from './ProfilePage';
import RecordingsPage from './RecordingsPage';
import ScheduleMeetingModal from './ScheduleMeetingModal';
/**
 * 内联 SVG 图标（与 Electron 客户端 jitsi-meet-electron 同源，保证视觉一致）。
 * 颜色由 currentColor 控制，主题切换无需额外适配。
 */
/** 加入会议：「+」线性图标。 */
function JoinIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M12 5v14M5 12h14', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '2.4' })));
}
/** 快速会议：闪电图标。 */
function QuickIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M13 3 5.5 13.5H12l-1 7.5L18.5 10.5H12L13 3Z', fill: 'currentColor' })));
}
/** 预定会议：日历+对勾图标。 */
function ScheduleIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '14', rx: '2', stroke: 'currentColor', strokeWidth: '2', width: '16', x: '4', y: '5' }),
        React.createElement("path", { d: 'M8 3v4M16 3v4M8.5 13.5l2.2 2.2 4.8-4.8', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
/** 创建会议：摄像机图标。 */
function CreateMeetingIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '12', rx: '2', stroke: 'currentColor', strokeWidth: '1.8', width: '14', x: '2', y: '6' }),
        React.createElement("path", { d: 'M16 10.5 21.5 7v10L16 13.5v-3Z', stroke: 'currentColor', strokeLinejoin: 'round', strokeWidth: '1.8' })));
}
/** 站内信：信封图标。 */
function MessageIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M3 7.5A2.5 2.5 0 0 1 5.5 5h13A2.5 2.5 0 0 1 21 7.5v9A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5v-9Z', stroke: 'currentColor', strokeWidth: '1.6' }),
        React.createElement("path", { d: 'm4 8 8 6 8-6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.6' })));
}
/** 关闭弹窗：X 图标。 */
function CloseIcon() {
    return (React.createElement("svg", { fill: 'none', height: '20', viewBox: '0 0 24 24', width: '20', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M18 6 6 18M6 6l12 12', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
/**
 * 侧栏底部「设置」齿轮图标：与会议内 settings drawer 触发的是同一个通用入口。
 * 路径取自 atlaskit/settings 图标，单行字符串避免 TS lexer 跨行解析坑。
 */
function SettingsNavIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z', fill: 'currentColor' })));
}
/** 会议首页导航图标。 */
function MeetingNavIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '12', rx: '2', stroke: 'currentColor', strokeWidth: '1.7', width: '16', x: '3', y: '5' }),
        React.createElement("path", { d: 'M8 19h8M12 17v2', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.7' })));
}
/** 录制导航图标。 */
function RecordingNavIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '12', cy: '12', fill: 'currentColor', r: '5' }),
        React.createElement("circle", { cx: '12', cy: '12', r: '8.5', stroke: 'currentColor', strokeWidth: '1.6' })));
}
/** 「我的」导航图标（人物剪影）。 */
function ProfileNavIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '12', cy: '8', r: '3.5', stroke: 'currentColor', strokeWidth: '1.7' }),
        React.createElement("path", { d: 'M5 19.5c1.8-3.2 4-4.8 7-4.8s5.2 1.6 7 4.8', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.7' })));
}
/** 会议行更多菜单。 */
function MoreIcon() {
    return (React.createElement("svg", { fill: 'currentColor', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '12', cy: '5', r: '1.8' }),
        React.createElement("circle", { cx: '12', cy: '12', r: '1.8' }),
        React.createElement("circle", { cx: '12', cy: '19', r: '1.8' })));
}
/** 会议行分享 / 弹出。 */
function ShareOutIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M14 5h5v5M19 5l-8 8', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.8' }),
        React.createElement("path", { d: 'M19 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4.5', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.8' })));
}
/** 历史列表右箭头。 */
function ChevronIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M9 6l6 6-6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
/** 首页日期旁日历图标。 */
function CalendarIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '14', rx: '2', stroke: 'currentColor', strokeWidth: '1.8', width: '16', x: '4', y: '5' }),
        React.createElement("path", { d: 'M8 3v4M16 3v4M4 10h16', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.8' })));
}
/**
 * 首页空态：咖啡杯插画（对齐参考图，仅占中间区域）.
 *
 * @returns {React.ReactElement}
 */
function CoffeeEmptyIllustration() {
    return (React.createElement("svg", { "aria-hidden": true, className: 'portal-empty-coffee', fill: 'none', viewBox: '0 0 220 170', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M92 18c0 10-6 14-6 24', opacity: '0.55', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '3.2' }),
        React.createElement("path", { d: 'M110 12c0 12-7 16-7 28', opacity: '0.7', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '3.4' }),
        React.createElement("path", { d: 'M128 18c0 10-6 14-6 24', opacity: '0.55', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '3.2' }),
        React.createElement("path", { d: 'M58 58h92c6 0 10 5 9 11l-10 72c-1 8-8 14-16 14H75c-8 0-15-6-16-14L49 69c-1-6 3-11 9-11Z', fill: 'currentColor', opacity: '0.22' }),
        React.createElement("path", { d: 'M58 58h92c6 0 10 5 9 11l-10 72c-1 8-8 14-16 14H75c-8 0-15-6-16-14L49 69c-1-6 3-11 9-11Z', opacity: '0.85', stroke: 'currentColor', strokeLinejoin: 'round', strokeWidth: '3.2' }),
        React.createElement("path", { d: 'M159 72c18 2 28 16 22 34-5 16-20 22-34 20', opacity: '0.85', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '3.2' }),
        React.createElement("ellipse", { cx: '108', cy: '158', fill: 'currentColor', opacity: '0.18', rx: '72', ry: '9' }),
        React.createElement("path", { d: 'M42 150c18-8 50-12 66-12s48 4 70 12', opacity: '0.85', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '3.2' }),
        React.createElement("rect", { fill: 'currentColor', height: '16', opacity: '0.28', rx: '2', width: '16', x: '148', y: '136' }),
        React.createElement("rect", { fill: 'currentColor', height: '12', opacity: '0.2', rx: '2', width: '12', x: '160', y: '128' })));
}
/**
 * 门户首页：对齐 Electron 能力，响应式布局。
 * 手机端仿主流会议 App：顶栏四按钮 + 底栏「会议 / 录像记录 / 我的」。
 */
export default function HomePage({ onLogout, onOpenSettings, onThemeChange, onUserUpdated, user }) {
    const isMobile = useIsPortalMobile();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [allItems, setAllItems] = useState([]);
    const [allPage, setAllPage] = useState(0);
    const [allHasMore, setAllHasMore] = useState(false);
    const [loadingMoreAll, setLoadingMoreAll] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);
    const [historyRecordings, setHistoryRecordings] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [pendingRecordingId, setPendingRecordingId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [modal, setModal] = useState(null);
    const [activePane, setActivePane] = useState('home');
    const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
    const [deletionCancelledNotice, setDeletionCancelledNotice] = useState(false);
    const [moreMenuId, setMoreMenuId] = useState(null);
    const [moreMenuPos, setMoreMenuPos] = useState(null);
    const [listHint, setListHint] = useState('');
    const token = () => getAccessToken() || '';
    const ALL_MEETINGS_PAGE_SIZE = 50;
    const loadAll = useCallback(async () => {
        try {
            const list = await portalApi.allMeetings(token(), 0, ALL_MEETINGS_PAGE_SIZE);
            // API 异常时可能返回非数组（如 nginx HTML），避免后续 .map 崩溃
            const items = Array.isArray(list) ? list : [];
            setAllItems(items);
            setAllPage(0);
            setAllHasMore(items.length >= ALL_MEETINGS_PAGE_SIZE);
            if (!Array.isArray(list)) {
                logger.warn('加载全部会议返回非数组，已降级为空列表');
            }
        }
        catch (e) {
            logger.warn(`加载全部会议失败 | ${e?.message}`);
            setError(e?.message || M.LOAD_FAILED);
        }
    }, []);
    const loadMoreAll = useCallback(async () => {
        if (loadingMoreAll || !allHasMore) {
            return;
        }
        const nextPage = allPage + 1;
        setLoadingMoreAll(true);
        try {
            const list = await portalApi.allMeetings(token(), nextPage, ALL_MEETINGS_PAGE_SIZE);
            const items = Array.isArray(list) ? list : [];
            setAllItems(prev => prev.concat(items));
            setAllPage(nextPage);
            setAllHasMore(items.length >= ALL_MEETINGS_PAGE_SIZE);
        }
        catch (e) {
            logger.warn(`加载更多会议失败 | ${e?.message}`);
        }
        finally {
            setLoadingMoreAll(false);
        }
    }, [allPage, allHasMore, loadingMoreAll]);
    const loadUnread = useCallback(async () => {
        try {
            const result = await portalApi.unreadCount(token());
            setUnreadCount(result?.count || 0);
        }
        catch (e) {
            logger.debug(`未读数拉取失败 | ${e?.message}`);
        }
    }, []);
    useEffect(() => {
        loadAll();
        loadUnread();
        const timer = window.setInterval(loadUnread, NOTIFICATION_POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [loadAll, loadUnread]);
    useEffect(() => {
        if (consumeDeletionCancelled()) {
            setDeletionCancelledNotice(true);
            logger.info(`[门户首页] ${new Date().toISOString()} 展示已取消注销提示`);
        }
    }, []);
    const run = async (action) => {
        setBusy(true);
        setError('');
        try {
            await action();
        }
        catch (e) {
            const message = e instanceof PortalApiError
                ? e.message
                : (e?.message || M.ACTION_FAILED);
            setError(message);
            logger.warn(`门户操作失败 | ${message}`);
        }
        finally {
            setBusy(false);
        }
    };
    const openMeeting = (session, joinOptions) => {
        navigateToMeeting(session, {
            displayName: joinOptions?.displayName || user.displayName,
            startSilent: joinOptions?.startSilent,
            startWithAudioMuted: joinOptions?.startWithAudioMuted,
            startWithVideoMuted: joinOptions?.startWithVideoMuted
        });
    };
    const onQuick = () => run(async () => {
        const meetingNo = generateMeetingNo(getDefaultMeetingNoLength());
        const session = await portalApi.quick(token(), { meetingNo });
        openMeeting(session);
    });
    const onCreateSubmit = (payload) => {
        run(async () => {
            const session = await portalApi.quick(token(), {
                meetingNo: payload.meetingNo,
                title: payload.title
            });
            setModal(null);
            openMeeting(session, {
                displayName: payload.displayName || user.displayName,
                startSilent: payload.startSilent,
                startWithAudioMuted: payload.startWithAudioMuted,
                startWithVideoMuted: payload.startWithVideoMuted
            });
        });
    };
    const onJoinSubmit = (payload) => {
        run(async () => {
            const session = await withDeviceJoinPrompt(flags => portalApi.join(token(), payload.meetingNo, flags));
            if (!session) {
                return;
            }
            setModal(null);
            openMeeting(session, {
                displayName: payload.displayName || user.displayName,
                startSilent: payload.startSilent,
                startWithAudioMuted: payload.startWithAudioMuted,
                startWithVideoMuted: payload.startWithVideoMuted
            });
        });
    };
    const onScheduleSubmit = (payload) => {
        run(async () => {
            await portalApi.schedule(token(), payload);
            setModal(null);
            await loadAll();
        });
    };
    const openMeetingDetail = (item) => {
        logger.info(`[门户] ${new Date().toISOString()} 打开会议详情`
            + ` | meetingId=${item.meetingId} status=${item.status || ''} meetingNo=${item.meetingNo || ''}`);
        setDetailItem(item);
        setError('');
    };
    const closeMeetingDetail = useCallback(() => {
        logger.info(`[门户] ${new Date().toISOString()} 关闭会议详情`);
        setDetailItem(null);
        setError('');
    }, []);
    const openHistory = () => run(async () => {
        const [items, recordings] = await Promise.all([
            portalApi.history(token()),
            portalApi.listRecordings(token()).catch((e) => {
                logger.warn(`加载历史时获取录制列表失败 | ${e?.message || e}`);
                return [];
            })
        ]);
        setHistoryItems(Array.isArray(items) ? items : []);
        setHistoryRecordings(Array.isArray(recordings) ? recordings : []);
        setHistoryOpen(true);
        logger.info(`打开会议历史 | count=${Array.isArray(items) ? items.length : 0}`
            + ` recordings=${Array.isArray(recordings) ? recordings.length : 0}`
            + ` desktopModal=${!isMobile}`);
    });
    const openMessages = () => run(async () => {
        const items = await portalApi.listNotifications(token());
        setMessages(Array.isArray(items) ? items : []);
        setModal('messages');
        await loadUnread();
    });
    const enterAllMeeting = (item) => {
        if (!item.joinable || !item.meetingNo) {
            setError('会议已结束');
            return;
        }
        const meetingNo = item.meetingNo;
        const meetingId = item.meetingId;
        run(async () => {
            const session = item.source === 'organizer'
                ? await withDeviceJoinPrompt(flags => portalApi.enter(token(), meetingId, flags))
                : await withDeviceJoinPrompt(flags => portalApi.join(token(), meetingNo, flags));
            if (!session) {
                return;
            }
            openMeeting(session);
        });
    };
    const deleteMeeting = (id, fromHistory = false) => {
        if (!window.confirm(M.CONFIRM_DELETE)) {
            return;
        }
        run(async () => {
            logger.info(`删除会议 | meetingId=${id} fromHistory=${fromHistory}`);
            await portalApi.deleteMeeting(token(), id);
            if (detailItem?.meetingId === id) {
                setDetailItem(null);
            }
            await loadAll();
            if (fromHistory || historyOpen) {
                const items = await portalApi.history(token());
                setHistoryItems(Array.isArray(items) ? items : []);
            }
        });
    };
    const enterFromHistory = (item) => {
        if (!canRejoinHistory(item) || !item.meetingId) {
            setError(M.MEETING_ENDED);
            logger.info(`历史会议不可重新入会 | meetingId=${item.meetingId} joinable=${item.joinable}`);
            return;
        }
        run(async () => {
            logger.info(`从历史重新入会 | meetingId=${item.meetingId} meetingNo=${item.meetingNo || ''}`
                + ` organizer=${Boolean(item.organizer)} joinable=${Boolean(item.joinable)}`);
            const session = await withDeviceJoinPrompt(flags => portalApi.rejoin(token(), item.meetingId, flags));
            if (!session) {
                return;
            }
            openMeeting(session);
        });
    };
    const markAllRead = () => run(async () => {
        await portalApi.markAllRead(token());
        await openMessages();
    });
    const closeModal = useCallback(() => {
        setModal(null);
        setError('');
    }, []);
    const closeHistory = useCallback(() => {
        logger.info(`[${new Date().toISOString()}] 关闭历史会议页`);
        setHistoryOpen(false);
        setError('');
    }, []);
    /** 首页分区：进行中 / 预定（后端 all 已排除 ENDED）。 */
    const ongoingItems = useMemo(() => allItems.filter(item => item.status === 'ONGOING'), [allItems]);
    const scheduledItems = useMemo(() => allItems.filter(item => item.status === 'SCHEDULED'), [allItems]);
    const meetingDateGroups = useMemo(() => groupPortalMeetingsByDate([...ongoingItems, ...scheduledItems], M.MEETING_TODAY), [ongoingItems, scheduledItems]);
    const showListHint = (text) => {
        setListHint(text);
        window.setTimeout(() => setListHint(''), 1800);
    };
    const copyMeetingInvite = (item) => {
        const timeRange = formatTimeRangeHm(item.scheduledStart || item.createdAt, item.scheduledEnd);
        const meetingNoText = formatMeetingNo(item.meetingNo, ' ') || item.meetingNo || '';
        const link = item.meetingNo ? `${window.location.origin}/${item.meetingNo}` : '';
        const lines = [
            item.title || item.meetingNo || M.NO_TITLE,
            timeRange,
            meetingNoText ? `${M.MEETING_NO}：${meetingNoText}` : '',
            link
        ].filter(Boolean);
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            logger.info(`[门户首页] ${new Date().toISOString()} 已复制会议邀请 | meetingId=${item.meetingId}`);
            showListHint(M.MEETING_SHARE_COPIED);
        }).catch((e) => {
            logger.warn(`复制会议邀请失败 | meetingId=${item.meetingId} ${e?.message || e}`);
        });
    };
    /** 渲染单条会议：电脑横向无边框；手机保留进行中/待开始。 */
    const renderMeetingItem = (item) => {
        const status = item.status || '';
        const isOngoing = status === 'ONGOING';
        const mobileStatusText = isOngoing ? M.MEETING_ONGOING : M.MEETING_PENDING;
        const timeRange = formatTimeRangeHm(item.scheduledStart || item.createdAt, item.scheduledEnd);
        const meetingNoText = formatMeetingNo(item.meetingNo, ' ') || item.meetingNo || '';
        const metaParts = [timeRange, meetingNoText].filter(Boolean);
        const menuOpen = moreMenuId === item.id;
        return (React.createElement("li", { className: menuOpen ? 'is-menu-open' : undefined, key: item.id },
            React.createElement("div", { className: 'portal-meeting-row' },
                React.createElement("button", { className: 'portal-meeting-item', onClick: () => openMeetingDetail(item), type: 'button' },
                    React.createElement("div", { className: 'portal-meeting-item-main' },
                        React.createElement("div", { className: 'title' }, item.title || item.meetingNo || M.NO_TITLE),
                        React.createElement("div", { className: 'meta' }, metaParts.join(' · ')),
                        React.createElement("div", { className: `portal-meeting-item-status ${isOngoing ? 'is-ongoing' : 'is-pending'}` }, mobileStatusText))),
                React.createElement("div", { className: 'portal-meeting-toolbar' },
                    React.createElement("div", { className: 'portal-meeting-hover-actions' },
                        React.createElement("button", { "aria-label": M.HISTORY_MORE, className: 'portal-meeting-hover-btn', onClick: event => {
                                event.stopPropagation();
                                if (menuOpen) {
                                    setMoreMenuId(null);
                                    setMoreMenuPos(null);
                                    return;
                                }
                                const rect = event.currentTarget.getBoundingClientRect();
                                const menuWidth = 132;
                                const menuHeight = 92;
                                let left = rect.left;
                                let top = rect.bottom + 6;
                                if (left + menuWidth > window.innerWidth - 8) {
                                    left = Math.max(8, rect.right - menuWidth);
                                }
                                if (top + menuHeight > window.innerHeight - 8) {
                                    top = Math.max(8, rect.top - menuHeight - 6);
                                }
                                logger.info(`[门户首页] ${new Date().toISOString()} 打开会议更多菜单`
                                    + ` | meetingId=${item.meetingId}`);
                                setMoreMenuPos({ left, top });
                                setMoreMenuId(item.id);
                            }, type: 'button' },
                            React.createElement(MoreIcon, null)),
                        React.createElement("button", { "aria-label": M.MEETING_SHARE, className: 'portal-meeting-hover-btn', onClick: event => {
                                event.stopPropagation();
                                copyMeetingInvite(item);
                            }, type: 'button' },
                            React.createElement(ShareOutIcon, null)),
                        menuOpen && moreMenuPos ? (React.createElement("div", { className: 'portal-meeting-more-menu', style: {
                                left: moreMenuPos.left,
                                top: moreMenuPos.top
                            } },
                            React.createElement("button", { onClick: event => {
                                    event.stopPropagation();
                                    setMoreMenuId(null);
                                    setMoreMenuPos(null);
                                    openMeetingDetail(item);
                                }, type: 'button' }, M.MEETING_DETAIL),
                            React.createElement("button", { className: 'danger', onClick: event => {
                                    event.stopPropagation();
                                    setMoreMenuId(null);
                                    setMoreMenuPos(null);
                                    deleteMeeting(item.meetingId);
                                }, type: 'button' }, M.DELETE_MEETING))) : null),
                    isOngoing ? (React.createElement("button", { className: 'portal-meeting-joined', disabled: busy || !item.joinable || !item.meetingNo, onClick: event => {
                            event.stopPropagation();
                            enterAllMeeting(item);
                        }, type: 'button' }, M.MEETING_JOINED)) : (React.createElement(React.Fragment, null,
                        React.createElement("button", { className: 'portal-meeting-joined is-desktop-only', disabled: busy || !item.joinable || !item.meetingNo, onClick: event => {
                                event.stopPropagation();
                                enterAllMeeting(item);
                            }, type: 'button' }, M.ENTER_MEETING),
                        React.createElement("span", { className: 'portal-meeting-item-chevron' },
                            React.createElement(ChevronIcon, null))))))));
    };
    /** 关闭电脑端「我的」浮层。 */
    const closeAvatarMenu = useCallback(() => {
        setAvatarMenuOpen(false);
    }, []);
    /** 点击侧栏头像打开「我的」浮层。 */
    const toggleAvatarMenu = useCallback(() => {
        setAvatarMenuOpen(prev => {
            const next = !prev;
            logger.info(`[门户首页] ${new Date().toISOString()} 「我的」浮层${next ? '打开' : '关闭'}`);
            return next;
        });
    }, []);
    /** 打开与会中同源的设置对话框。 */
    const openSettings = useCallback(() => {
        logger.info(`[门户] ${new Date().toISOString()} 打开设置`);
        setAvatarMenuOpen(false);
        onOpenSettings();
    }, [onOpenSettings]);
    /** 从头像菜单退出登录。 */
    const logoutFromMenu = useCallback(() => {
        logger.info('用户点击退出登录 | 入口=头像菜单');
        setAvatarMenuOpen(false);
        clearSession();
        onLogout();
    }, [onLogout]);
    useEffect(() => {
        if (!moreMenuId) {
            return;
        }
        const onPointerDown = (event) => {
            const target = event.target;
            if (target?.closest('.portal-meeting-hover-actions')) {
                return;
            }
            setMoreMenuId(null);
            setMoreMenuPos(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [moreMenuId]);
    useEffect(() => {
        if (!modal && !avatarMenuOpen && !historyOpen && !detailItem) {
            return;
        }
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (detailItem) {
                    closeMeetingDetail();
                }
                if (modal) {
                    closeModal();
                }
                if (historyOpen) {
                    closeHistory();
                }
                if (avatarMenuOpen) {
                    closeAvatarMenu();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [modal, avatarMenuOpen, historyOpen, detailItem, closeModal, closeHistory, closeAvatarMenu, closeMeetingDetail]);
    const recentMeetingNos = useMemo(() => {
        const seen = new Set();
        const nos = [];
        allItems.forEach(item => {
            const meetingNo = (item.meetingNo || '').replace(/\D/g, '');
            if (meetingNo && !seen.has(meetingNo)) {
                seen.add(meetingNo);
                nos.push(meetingNo);
            }
        });
        return nos;
    }, [allItems]);
    const avatarText = (user.displayName || user.phone || 'U').slice(0, 1).toUpperCase();
    const avatarSrc = userMediaUrl('avatar', user);
    /** 切换手机底栏 / 桌面侧栏页签。 */
    const switchPane = useCallback((pane) => {
        logger.info(`[门户首页] ${new Date().toISOString()} 切换页签 | pane=${pane}`);
        setActivePane(pane);
        setError('');
        if (pane !== 'recordings') {
            setPendingRecordingId(null);
        }
    }, []);
    /** 从「我的」打开录制：关闭浮层并跳转到录制页。 */
    const openRecordingsFromProfile = useCallback(() => {
        logger.info(`[门户首页] ${new Date().toISOString()} 从「我的」跳转录制页`);
        setAvatarMenuOpen(false);
        switchPane('recordings');
    }, [switchPane]);
    /**
     * 已结束会议：有录像则跳到对应录像详情。
     */
    const viewHistoryRecording = (item) => {
        const rec = findRecordingForMeeting(historyRecordings, item.meetingId);
        if (!rec) {
            logger.info(`历史会议无录像，不展示跳转 | meetingId=${item.meetingId}`);
            return;
        }
        logger.info(`从历史打开录像 | meetingId=${item.meetingId} recordingId=${rec.id}`);
        setPendingRecordingId(rec.id);
        setHistoryOpen(false);
        setActivePane('recordings');
        setError('');
    };
    return (React.createElement("div", { className: `portal-home${historyOpen ? ' is-history' : ''}`, onClick: closeAvatarMenu },
        React.createElement("aside", { className: 'portal-side-nav' },
            React.createElement("div", { className: 'portal-avatar-menu-wrap', onClick: event => event.stopPropagation() },
                React.createElement("button", { "aria-expanded": avatarMenuOpen, "aria-haspopup": 'dialog', "aria-label": M.OPEN_ACCOUNT_MENU, className: 'portal-avatar', onClick: toggleAvatarMenu, title: M.OPEN_ACCOUNT_MENU, type: 'button' },
                    avatarSrc ? (React.createElement("img", { alt: '', onError: event => {
                            event.currentTarget.style.display = 'none';
                        }, src: avatarSrc })) : null,
                    avatarText),
                avatarMenuOpen ? (React.createElement(React.Fragment, null,
                    React.createElement("div", { "aria-hidden": true, className: 'portal-profile-overlay-backdrop', onClick: closeAvatarMenu }),
                    React.createElement("div", { "aria-label": M.PROFILE_TITLE, className: 'portal-profile-overlay', role: 'dialog' },
                        React.createElement(ProfilePage, { onLogout: logoutFromMenu, onOpenRecordings: openRecordingsFromProfile, onOpenSettings: openSettings, onThemeChange: onThemeChange, onUserUpdated: onUserUpdated, openInnerAsModal: true, user: user })))) : null),
            React.createElement("button", { className: `portal-nav-btn${activePane === 'home' ? ' active' : ''}`, onClick: () => switchPane('home'), title: M.NAV_MEETING, type: 'button' },
                React.createElement(MeetingNavIcon, null)),
            React.createElement("button", { className: `portal-nav-btn${activePane === 'recordings' ? ' active' : ''}`, onClick: () => {
                    setPendingRecordingId(null);
                    switchPane('recordings');
                }, title: M.NAV_RECORDINGS, type: 'button' },
                React.createElement(RecordingNavIcon, null)),
            React.createElement("div", { className: 'portal-side-nav-footer' },
                React.createElement("button", { "aria-label": M.MESSAGES, className: 'portal-nav-btn', onClick: openMessages, title: M.MESSAGES, type: 'button' },
                    React.createElement(MessageIcon, null),
                    unreadCount > 0 ? React.createElement("span", { className: 'badge' }, unreadCount) : null),
                React.createElement("button", { "aria-label": M.SETTINGS, className: 'portal-nav-btn', onClick: openSettings, title: M.SETTINGS, type: 'button' },
                    React.createElement(SettingsNavIcon, null)))),
        React.createElement("main", { className: 'portal-main' }, isMobile && historyOpen ? (React.createElement(HistoryPage, { busy: busy, error: error, items: historyItems, onBack: closeHistory, onDelete: meetingId => deleteMeeting(meetingId, true), onRefresh: openHistory, onRejoin: enterFromHistory, onViewRecording: viewHistoryRecording, recordings: historyRecordings })) : activePane === 'recordings' ? (React.createElement(RecordingsPage, { initialRecordingId: pendingRecordingId, onBack: () => switchPane('home') })) : activePane === 'profile' ? (React.createElement(ProfilePage, { onLogout: onLogout, onOpenRecordings: openRecordingsFromProfile, onOpenSettings: onOpenSettings, onThemeChange: onThemeChange, onUserUpdated: onUserUpdated, user: user })) : (React.createElement(React.Fragment, null,
            deletionCancelledNotice ? (React.createElement("div", { className: 'portal-account-notice', role: 'status' },
                React.createElement("span", null, M.DELETION_CANCELLED),
                React.createElement("button", { "aria-label": M.CLOSE, onClick: () => setDeletionCancelledNotice(false), type: 'button' }, '×'))) : null,
            React.createElement("header", { className: 'portal-mobile-home-header' },
                React.createElement("button", { className: 'portal-mobile-home-user', onClick: () => switchPane('profile'), type: 'button' },
                    React.createElement("span", { className: 'portal-avatar portal-avatar-sm' },
                        avatarSrc ? (React.createElement("img", { alt: '', onError: event => {
                                event.currentTarget.style.display = 'none';
                            }, src: avatarSrc })) : null,
                        avatarText),
                    React.createElement("span", { className: 'portal-mobile-home-user-text' },
                        React.createElement("span", { className: 'name' }, user.displayName || user.phone),
                        user.phone && user.displayName ? (React.createElement("span", { className: 'sub' }, user.phone)) : null)),
                React.createElement("button", { "aria-label": M.MESSAGES, className: 'portal-mobile-msg-btn', onClick: openMessages, title: M.MESSAGES, type: 'button' },
                    React.createElement(MessageIcon, null),
                    unreadCount > 0 ? React.createElement("span", { className: 'badge' }, unreadCount) : null)),
            React.createElement("header", { className: 'portal-main-header' },
                React.createElement("div", null,
                    React.createElement("h2", null, user.displayName || user.phone),
                    React.createElement("p", null, user.phone))),
            React.createElement("div", { className: 'portal-desktop-home' },
                React.createElement("div", { className: 'portal-home-cluster' },
                    React.createElement("div", { className: 'portal-actions-pane' },
                        React.createElement("div", { className: 'portal-actions' },
                            React.createElement("button", { disabled: busy, onClick: () => {
                                    setError('');
                                    setModal('join');
                                }, type: 'button' },
                                React.createElement("span", { className: 'icon-wrap' },
                                    React.createElement(JoinIcon, null)),
                                React.createElement("span", { className: 'label' }, M.JOIN_MEETING)),
                            React.createElement("button", { disabled: busy, onClick: onQuick, type: 'button' },
                                React.createElement("span", { className: 'icon-wrap' },
                                    React.createElement(QuickIcon, null)),
                                React.createElement("span", { className: 'label' }, M.QUICK_MEETING)),
                            React.createElement("button", { disabled: busy, onClick: () => {
                                    setError('');
                                    setModal('schedule');
                                }, type: 'button' },
                                React.createElement("span", { className: 'icon-wrap' },
                                    React.createElement(ScheduleIcon, null)),
                                React.createElement("span", { className: 'label' }, M.SCHEDULE_MEETING)),
                            React.createElement("button", { disabled: busy, onClick: () => {
                                    setError('');
                                    setModal('create');
                                }, type: 'button' },
                                React.createElement("span", { className: 'icon-wrap' },
                                    React.createElement(CreateMeetingIcon, null)),
                                React.createElement("span", { className: 'label' }, M.CREATE_MEETING))),
                        error ? React.createElement("div", { className: 'portal-error inline' }, error) : null),
                    React.createElement("div", { "aria-hidden": 'true', className: 'portal-home-divider' }),
                    React.createElement("section", { className: 'portal-upcoming' },
                        React.createElement("div", { className: 'portal-section-head portal-home-list-head' },
                            React.createElement("div", { className: 'portal-home-date-block' },
                                React.createElement(CalendarIcon, null),
                                React.createElement("div", { className: 'portal-home-date-texts' },
                                    React.createElement("div", { className: 'portal-home-date' },
                                        React.createElement("span", { className: 'portal-home-date-today-prefix' }, `${M.MEETING_TODAY} `),
                                        formatHomeTodayLabel()),
                                    React.createElement("div", { className: 'portal-home-date-sub' }, formatHomeWeekdayLabel()))),
                            React.createElement("button", { className: 'portal-history-entry', onClick: openHistory, type: 'button' },
                                React.createElement("span", { className: 'portal-history-entry-desktop' }, M.ALL_MEETINGS),
                                React.createElement("span", { className: 'portal-history-entry-mobile' }, M.HISTORY),
                                React.createElement(ChevronIcon, null))),
                        meetingDateGroups.length > 0 ? (meetingDateGroups.map(group => (React.createElement("div", { className: 'portal-meeting-date-group', key: group.key },
                            React.createElement("div", { className: 'portal-meeting-date-label' }, group.label),
                            React.createElement("ul", { className: 'portal-list' }, group.items.map(renderMeetingItem)))))) : (React.createElement("div", { className: 'portal-empty portal-empty-meetings' },
                            React.createElement(CoffeeEmptyIllustration, null),
                            React.createElement("div", { className: 'portal-empty-text' }, M.NO_MEETINGS))),
                        allHasMore ? (React.createElement("div", { className: 'portal-load-more' },
                            React.createElement("button", { disabled: loadingMoreAll, onClick: loadMoreAll, type: 'button' }, loadingMoreAll ? M.LOADING_MORE : M.LOAD_MORE))) : null)))))),
        !historyOpen && !detailItem ? (React.createElement("nav", { "aria-label": '\u4E3B\u5BFC\u822A', className: 'portal-mobile-tabbar' },
            React.createElement("button", { className: activePane === 'home' ? 'active' : '', onClick: () => switchPane('home'), type: 'button' },
                React.createElement(MeetingNavIcon, null),
                React.createElement("span", null, M.NAV_MEETING)),
            React.createElement("button", { className: activePane === 'recordings' ? 'active' : '', onClick: () => {
                    setPendingRecordingId(null);
                    switchPane('recordings');
                }, type: 'button' },
                React.createElement(RecordingNavIcon, null),
                React.createElement("span", null, M.NAV_RECORDINGS_TAB)),
            React.createElement("button", { className: activePane === 'profile' ? 'active' : '', onClick: () => switchPane('profile'), type: 'button' },
                React.createElement(ProfileNavIcon, null),
                React.createElement("span", null, M.NAV_PROFILE)))) : null,
        detailItem ? (React.createElement(MeetingDetailPage, { busy: busy, item: detailItem, onBack: closeMeetingDetail, onDelete: meetingId => deleteMeeting(meetingId), onEnter: enterAllMeeting, user: user })) : null,
        !isMobile && historyOpen ? (React.createElement("div", { className: 'portal-modal-backdrop', onClick: closeHistory, role: 'presentation' },
            React.createElement("div", { className: 'portal-modal portal-history-modal', onClick: event => event.stopPropagation(), role: 'dialog' },
                React.createElement(HistoryPage, { busy: busy, error: error, items: historyItems, onBack: closeHistory, onDelete: meetingId => deleteMeeting(meetingId, true), onRefresh: openHistory, onRejoin: enterFromHistory, onViewRecording: viewHistoryRecording, recordings: historyRecordings })))) : null,
        modal === 'join' && (React.createElement(JoinMeetingForm, { busy: busy, defaultDisplayName: user.displayName || '', error: error, isMobile: isMobile, onClose: closeModal, onJoin: onJoinSubmit, recentMeetingNos: recentMeetingNos })),
        modal === 'create' && (React.createElement(CreateMeetingForm, { busy: busy, defaultDisplayName: user.displayName || '', error: error, isMobile: isMobile, onClose: closeModal, onCreate: onCreateSubmit })),
        modal === 'schedule' && (React.createElement(ScheduleMeetingModal, { busy: busy, displayName: user.displayName, error: error, onClose: closeModal, onSubmit: onScheduleSubmit })),
        modal === 'messages' && (React.createElement("div", { className: 'portal-modal-backdrop', onClick: closeModal, role: 'presentation' },
            React.createElement("div", { className: 'portal-modal wide', onClick: e => e.stopPropagation() },
                React.createElement("div", { className: 'portal-modal-header' },
                    React.createElement("h3", null, M.MESSAGES),
                    React.createElement("div", { className: 'portal-modal-header-actions' },
                        React.createElement("button", { disabled: busy, onClick: markAllRead, type: 'button' }, M.MARK_ALL_READ),
                        React.createElement("button", { className: 'portal-modal-close', onClick: closeModal, title: M.CLOSE, type: 'button' },
                            React.createElement(CloseIcon, null)))),
                messages.length === 0 ? (React.createElement("div", { className: 'portal-empty' }, M.NO_MESSAGES)) : (React.createElement("ul", { className: 'portal-list' }, messages.map(item => (React.createElement("li", { key: item.id },
                    React.createElement("div", { className: 'title' }, item.title),
                    React.createElement("div", { className: 'meta' }, item.content)))))),
                React.createElement("div", { className: 'portal-modal-actions' },
                    React.createElement("button", { onClick: closeModal, type: 'button' }, M.CLOSE))))),
        listHint ? (React.createElement("div", { className: 'portal-list-hint', role: 'status' }, listHint)) : null));
}
